import { Color, Point, Rect, rgb, unpackRgb } from '../core/types';

/**
 * Multi-stop gradients evaluated per terminal cell.
 *
 * Interpolation happens in linear-light space rather than straight sRGB: a
 * green-to-blue ramp mixed naively goes muddy in the middle, which is very
 * visible when a gradient is only 40 cells wide.
 */

export type GradientKind = 'horizontal' | 'vertical' | 'diagonal' | 'radial' | 'conic';
export type GradientTarget = 'fg' | 'bg' | 'both';

export interface GradientStop {
  /** 0..1 along the gradient. */
  position: number;
  color: Color;
}

export interface GradientDef {
  kind: GradientKind;
  stops: GradientStop[];
  target: GradientTarget;
  /** Shifts the ramp; used by animated gradients. 0..1 wraps. */
  offset?: number;
  /** Mirrors the ramp back on itself (ping-pong look). */
  mirror?: boolean;
  /** Angle in degrees for the diagonal kind. */
  angle?: number;
}

export const DEFAULT_GRADIENT: GradientDef = {
  kind: 'horizontal',
  target: 'fg',
  stops: [
    { position: 0, color: 0x32ff94 },
    { position: 0.5, color: 0x35c9d6 },
    { position: 1, color: 0x4477ff },
  ],
  offset: 0,
  mirror: false,
  angle: 45,
};

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(v: number): number {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(c * 255)));
}

export function mixColors(a: Color, b: Color, t: number): Color {
  const k = Math.max(0, Math.min(1, t));
  const [ar, ag, ab] = unpackRgb(a);
  const [br, bg, bb] = unpackRgb(b);
  return rgb(
    linearToSrgb(srgbToLinear(ar) + (srgbToLinear(br) - srgbToLinear(ar)) * k),
    linearToSrgb(srgbToLinear(ag) + (srgbToLinear(bg) - srgbToLinear(ag)) * k),
    linearToSrgb(srgbToLinear(ab) + (srgbToLinear(bb) - srgbToLinear(ab)) * k),
  );
}

/** Colour at position `t` (0..1) along a stop list. */
export function sampleStops(stops: readonly GradientStop[], t: number): Color {
  if (stops.length === 0) return 0;
  if (stops.length === 1) return stops[0].color;
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const k = Math.max(0, Math.min(1, t));
  if (k <= sorted[0].position) return sorted[0].color;
  const last = sorted[sorted.length - 1];
  if (k >= last.position) return last.color;
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1];
    const b = sorted[i];
    if (k <= b.position) {
      const span = b.position - a.position;
      return span <= 0 ? b.color : mixColors(a.color, b.color, (k - a.position) / span);
    }
  }
  return last.color;
}

/**
 * Normalised position of a cell within the gradient's geometry.
 * `from`/`to` are the drag endpoints for linear kinds, or centre/edge for radial.
 */
export function gradientPosition(
  def: GradientDef,
  x: number,
  y: number,
  from: Point,
  to: Point,
): number {
  let t: number;
  switch (def.kind) {
    case 'horizontal': {
      const span = to.x - from.x;
      t = span === 0 ? 0 : (x - from.x) / span;
      break;
    }
    case 'vertical': {
      const span = to.y - from.y;
      t = span === 0 ? 0 : (y - from.y) / span;
      break;
    }
    case 'diagonal': {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const lenSq = dx * dx + dy * dy;
      t = lenSq === 0 ? 0 : ((x - from.x) * dx + (y - from.y) * dy) / lenSq;
      break;
    }
    case 'radial': {
      // Cells are about twice as tall as wide, so vertical distance counts
      // double to keep a radial gradient looking circular on screen.
      const dx = x - from.x;
      const dy = (y - from.y) * 2;
      const rx = to.x - from.x;
      const ry = (to.y - from.y) * 2;
      const radius = Math.sqrt(rx * rx + ry * ry) || 1;
      t = Math.sqrt(dx * dx + dy * dy) / radius;
      break;
    }
    case 'conic': {
      const angle = Math.atan2((y - from.y) * 2, x - from.x);
      t = (angle + Math.PI) / (Math.PI * 2);
      break;
    }
  }

  t += def.offset ?? 0;
  if (def.mirror) {
    const wrapped = ((t % 2) + 2) % 2;
    t = wrapped > 1 ? 2 - wrapped : wrapped;
  } else if ((def.offset ?? 0) !== 0) {
    t = ((t % 1) + 1) % 1;
  }
  return Math.max(0, Math.min(1, t));
}

export function sampleGradient(
  def: GradientDef,
  x: number,
  y: number,
  from: Point,
  to: Point,
): Color {
  return sampleStops(def.stops, gradientPosition(def, x, y, from, to));
}

/** Evaluates a gradient over a rectangle, one colour per cell. */
export function renderGradient(
  def: GradientDef,
  area: Rect,
  from: Point,
  to: Point,
): Array<{ x: number; y: number; color: Color }> {
  const out: Array<{ x: number; y: number; color: Color }> = [];
  for (let y = area.y; y < area.y + area.height; y++) {
    for (let x = area.x; x < area.x + area.width; x++) {
      out.push({ x, y, color: sampleGradient(def, x, y, from, to) });
    }
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Animated gradients
 * ------------------------------------------------------------------ */

export type Easing = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'sine';

export interface GradientAnimation {
  enabled: boolean;
  /** Ramp offsets per second. */
  speed: number;
  direction: 'forward' | 'reverse';
  easing: Easing;
  fps: number;
  /** Starting offset, 0..1. */
  phase: number;
  mode: 'scroll' | 'pulse';
  pingPong: boolean;
}

export const DEFAULT_ANIMATION: GradientAnimation = {
  enabled: false,
  speed: 0.25,
  direction: 'forward',
  easing: 'sine',
  fps: 10,
  phase: 0,
  mode: 'scroll',
  pingPong: false,
};

export function ease(t: number, kind: Easing): number {
  const k = Math.max(0, Math.min(1, t));
  switch (kind) {
    case 'easeIn':
      return k * k;
    case 'easeOut':
      return 1 - (1 - k) * (1 - k);
    case 'easeInOut':
      return k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2;
    case 'sine':
      return 0.5 - Math.cos(k * Math.PI) / 2;
    default:
      return k;
  }
}

/** Gradient offset at time `seconds` for an animation definition. */
export function animatedOffset(anim: GradientAnimation, seconds: number): number {
  const dir = anim.direction === 'reverse' ? -1 : 1;
  const raw = anim.phase + seconds * anim.speed * dir;
  if (anim.mode === 'pulse') {
    const cycle = ((raw % 1) + 1) % 1;
    const t = anim.pingPong ? (cycle > 0.5 ? 1 - (cycle - 0.5) * 2 : cycle * 2) : cycle;
    return ease(t, anim.easing);
  }
  if (anim.pingPong) {
    const cycle = ((raw % 2) + 2) % 2;
    return cycle > 1 ? 2 - cycle : cycle;
  }
  return ((raw % 1) + 1) % 1;
}

/**
 * The offsets one full cycle of an animation visits, one per frame.
 *
 * Baking a moving gradient into frames needs the *sequence* of offsets rather
 * than the offset at a moment, and the sequence has to close the loop: the
 * frame after the last must land back on the first, or the animation jumps
 * every time it repeats. So the cycle is divided into `frames` steps and the
 * final step is left out — with 10 frames of a scroll that is 0.0 to 0.9, and
 * playing the tenth wraps to 0.0 by itself.
 */
export function offsetCycle(anim: GradientAnimation, frames: number): number[] {
  const count = Math.max(1, Math.floor(frames));
  const out: number[] = [];

  // One cycle in seconds. A scroll covers the ramp once; ping-pong covers it
  // out and back, so it takes twice as long to return to where it started.
  const speed = anim.speed === 0 ? 1 : Math.abs(anim.speed);
  const cycleSeconds = (anim.pingPong && anim.mode === 'scroll' ? 2 : 1) / speed;

  for (let i = 0; i < count; i++) {
    out.push(animatedOffset(anim, (i / count) * cycleSeconds));
  }
  return out;
}
